import { createSlice } from "@reduxjs/toolkit";

import * as Ether from "../shared/ether";
import * as Geocoder from "../shared/geocoder";
import * as Api from "../shared/imversed-api";

const initialState = {
  name: null,
  selected: null,
  owned: {
    ids: null,
    lands: null,
  },
  zones: [{ id: 0 }, { id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }, { id: 5 }],
};

const hubSlice = createSlice({
  name: "hub",
  initialState: initialState,
  reducers: {
    updateSelected: (state, action) => {
      state.selected = action.payload;
    },
    deselectLand: (state) => {
      state.selected = null;
    },
    updateOwnedIds: (state, action) => {
      state.owned.ids = action.payload;
    },
    updateOwnedLands: (state, action) => {
      state.owned.lands = action.payload;
    },
    updateName: (state, action) => {
      state.name = action.payload;
    },
    updateZones: (state, action) => {
      state.zones = action.payload;
    },
    resetOwned: (state) => {
      state.owned = initialState.owned;
    },
  },
});

const { updateSelected, deselectLand, updateOwnedIds, updateOwnedLands, updateName, updateZones, resetOwned } =
  hubSlice.actions;

const selectLand = (landId) => (dispatch, getState) => {
  dispatch(updateSelected({ id: landId }));

  Ether.getLand(landId)
    .then((land) => {
      const selected = getState().hub.selected;
      if (selected) {
        dispatch(
          updateSelected({
            ...selected,
            sold: land.sold,
            zone: land.zoneId,
            area: land.area.toNumber(),
          })
        );
      }

      dispatch(_loadGeo(land));
      dispatch(_loadMeta(land));

      if (land.sold) {
        dispatch(_checkOwner(landId));
      } else {
        dispatch(_loadPrice(landId));
      }
    })
    .catch((err) => {
      console.log("selectLand: %s", err);
      const selected = getState().hub.selected;
      dispatch(
        updateSelected({
          ...selected,
          meta: { error: "Couldn't load meta for land: " + landId },
        })
      );
    });
};

const _loadPrice = (tokenId) => (dispatch, getState) => {
  Ether.getLandPrice(tokenId)
    .then((price) => {
      const selected = getState().hub.selected;
      if (selected) {
        dispatch(updateSelected({ ...selected, price: price }));
      }

      Ether.getBuyLandEstimateGasPrice(tokenId, price)
        .then((gasPrice) => {
          const selected = getState().hub.selected;
          if (selected) {
            dispatch(updateSelected({ ...selected, gas: gasPrice }));
          }
        })
        .catch(() => {}); // It always fails when we use fallback provider
    })
    .catch((err) => console.log("landPrice: %s", err));
};

const _checkOwner = (tokenId) => (dispatch, getState) => {
  Ether.getLandOwner(tokenId)
    .then((address) => {
      const selected = getState().hub.selected;
      if (selected) dispatch(updateSelected({ ...selected, owner: address }));
    })
    .catch((err) => console.log("_checkOwner: %s", err));
};

const _loadGeo = (land) => (dispatch, getState) => {
  const multiplier = Math.pow(10, 6); // TODO: don't know how to share decimals 6 with blockchain
  const coordinates = [land.center.latitude / multiplier, land.center.longtitude / multiplier];
  Geocoder.decodeCoordinates(coordinates)
    .then((response) => {
      const feature = response.features[0];

      if (!feature) {
        const meta = { error: "Couldn't load geo location" };
        const selected = getState().hub.selected;
        if (selected) dispatch(updateSelected({ ...selected, geo: meta, zone: { error: "Couldn't load zone" } }));
      } else {
        const meta = {
          coordinates: coordinates,
          name: feature.place_name,
          place: feature.text,
        };
        const selected = getState().hub.selected;
        if (selected) dispatch(updateSelected({ ...selected, geo: meta }));
      }
    })
    .catch((err) => console.log("_loadGeo: %s", err));
};

const _loadMeta = (land) => (dispatch, getState) => {
  Api.getLandMeta(land.id.toString())
    .then((response) => {
      const data = response.data;
      const selected = getState().hub.selected;
      dispatch(updateSelected({ ...selected, meta: data }));
    })
    .catch((err) => console.log("_loadMeta: %s", err));
};

const buySelectedLand = () => (dispatch, getState) => {
  const selected = getState().hub.selected;
  if (!selected || selected.sold) {
    throw new Error("Couldn't buy selected land");
  }

  Ether.buyLand(selected.id, selected.price)
    .then((tx) => {
      dispatch(updateSelected({ ...selected, txHash: tx.hash }));
    })
    .catch((err) => console.log("buySelectedLand: %s", err));
};

const fetchOwnedIds = (address) => (dispatch) => {
  Ether.getOwnedTokenIds(address)
    .then((tokenIds) => {
      dispatch(updateOwnedIds(tokenIds));
    })
    .catch((err) => console.log("fetchOwnedIds: %s", err));
};

const fetchOwnedLands = (ids) => async (dispatch, getState) => {
  Ether.batchGetLands(ids)
    .then((response) => {
      var lands = response.map((land) => {
        return { id: land.id.toString(), zone: land.zoneId };
      });

      Api.getLandsMeta(lands.map((land) => land.id).join(";"))
        .then((response) => {
          lands = lands.map((land) => {
            const metadata = response.data.find((d) => d.id === land.id);
            if (!metadata) return land;

            return { ...land, meta: metadata };
          });

          dispatch(updateOwnedLands(lands));
        })
        .catch((err) => console.log("fetchOwnedLands from backend: %s", err));
    })
    .catch((err) => console.log("fetchOwnedLands from blockchain: %s", err));
};

const fetchHubDetails = () => (dispatch, getState) => {
  Ether.getHubName()
    .then((name) => dispatch(updateName(name)))
    .catch((err) => console.log(err));

  const zonesIds = getState().hub.zones.map((zone) => zone.id);
  Ether.getZones(zonesIds)
    .then((zones) => dispatch(updateZones(zones)))
    .catch((err) => console.log("fetchHubDetails: %s", err));
};

const setPlotMetadata = (metadata) => (dispatch, getState) => {
  const message = JSON.stringify(metadata);
  Ether.signMessage(message)
    .then((signature) => {
      Api.saveLandsMeta(metadata, signature).then(() => {
        // Update selected land
        const selected = getState().hub.selected;
        if (selected && selected.id === metadata.id) {
          dispatch(selectLand(selected.id));
        }
        // Update owned lands
        var ownedLands = getState().hub.owned.lands;
        ownedLands = ownedLands.map((land) => {
          if (land.id === metadata.id) {
            return { ...land, meta: metadata };
          }
          return land;
        });
        dispatch(updateOwnedLands(ownedLands));
      });
    })
    .catch((error) => {
      console.log(error);
    });
};

export {
  selectLand,
  deselectLand,
  buySelectedLand,
  fetchOwnedIds,
  fetchOwnedLands,
  fetchHubDetails,
  setPlotMetadata,
  resetOwned,
};

export default hubSlice.reducer;
